import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, FileText, Briefcase, Edit, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useClient } from '../../hooks/useClients';
import AddClientModal from './AddClientModal';
import ClientQuestionsManager from './ClientQuestionsManager';
import { formatDate } from '../../utils/dateFormatter';
import DetailContainer from '../ui/detail-container';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { client, rfqs, contacts, loading, error, refetch } = useClient(id);
  const [editModalOpen, setEditModalOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading client: {error || 'Not found'}</p>
        <Button onClick={() => navigate('/recruiter/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      'inclusion_requested': 'bg-yellow-100 text-yellow-800',
      'invited': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-indigo-100 text-indigo-800',
      'submitted': 'bg-purple-100 text-purple-800',
      'pending_outcome': 'bg-orange-100 text-orange-800',
      'successful': 'bg-green-100 text-green-800',
      'unsuccessful': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DetailContainer>
      {/* Header */}
      <DetailContainer.Header>
        <DetailContainer.TitleSection>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
            onClick={() => navigate('/recruiter/clients')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DetailContainer.Title
            title={`${client.name}${client.abbreviation ? ` (${client.abbreviation})` : ''}`}
            subtitle={client.abn || 'No ABN provided'}
          />
        </DetailContainer.TitleSection>
        <DetailContainer.Actions>
          <Badge
            className={
              client.status === 'Active'
                ? 'bg-green-100 text-green-800'
                : client.status === 'Pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            }
          >
            {client.status || 'Active'}
          </Badge>
          <Badge className={client.is_rcti_client ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
            {client.is_rcti_client ? 'RCTI' : 'Standard'}
          </Badge>
          <Button onClick={() => setEditModalOpen(true)} size="sm" className="md:size-default">
            <Edit className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Edit Client</span>
            <span className="sm:hidden">Edit</span>
          </Button>
        </DetailContainer.Actions>
      </DetailContainer.Header>

      {/* Client Info */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Client Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Company Name</p>
              <p className="text-sm sm:text-base text-foreground mt-1 break-words">{client.name}</p>
            </div>
            {client.abbreviation && (
              <div>
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Abbreviation</p>
                <p className="text-sm sm:text-base text-foreground mt-1">{client.abbreviation}</p>
              </div>
            )}
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">ABN</p>
              <p className="text-sm sm:text-base text-foreground mt-1">{client.abn || 'Not provided'}</p>
            </div>
            {client.primary_procurement_email && (
              <div>
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Primary Procurement Email</p>
                <p className="text-sm sm:text-base text-foreground mt-1">
                  <a href={`mailto:${client.primary_procurement_email}`} className="text-blue-600 hover:underline break-all">
                    {client.primary_procurement_email}
                  </a>
                </p>
              </div>
            )}
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">RCTI Client</p>
              <Badge className={`text-xs ${client.is_rcti_client ? 'bg-green-100 text-green-800' : 'bg-secondary text-secondary-foreground'}`}>
                {client.is_rcti_client ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Client Since</p>
              <p className="text-sm sm:text-base text-foreground mt-1">
                {client.created_at ? new Date(client.created_at).toLocaleDateString('en-AU', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                }) : 'Not specified'}
              </p>
            </div>
          </div>

          {client.notes && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-2">Notes</p>
              <p className="text-sm sm:text-base text-foreground whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Contacts */}
      {contacts && contacts.length > 0 && (
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Client Contacts</CardTitle>
            <CardDescription>{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="border rounded-lg p-3 sm:p-4">
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-medium text-foreground break-words">{contact.name}</p>
                      {contact.position && (
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{contact.position}</p>
                      )}
                    </div>
                    {contact.priority > 0 && (
                      <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">
                        Priority: {contact.priority}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    {contact.branch && (
                      <div>
                        <span className="text-muted-foreground">Branch: </span>
                        <span className="text-foreground break-words">{contact.branch}</span>
                      </div>
                    )}
                    {contact.division && (
                      <div>
                        <span className="text-muted-foreground">Division: </span>
                        <span className="text-foreground break-words">{contact.division}</span>
                      </div>
                    )}
                    {contact.email && (
                      <div>
                        <span className="text-muted-foreground">Email: </span>
                        <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline break-all">
                          {contact.email}
                        </a>
                      </div>
                    )}
                    {contact.phone && (
                      <div>
                        <span className="text-muted-foreground">Phone: </span>
                        <span className="text-foreground break-words">{contact.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Application Questions */}
      <ClientQuestionsManager clientId={id} />

      {/* RFQs */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">RFQs</CardTitle>
          <CardDescription>{rfqs.length} RFQ{rfqs.length !== 1 ? 's' : ''} for this client</CardDescription>
        </CardHeader>
        <CardContent>
          {rfqs.length > 0 ? (
            <div className="space-y-3">
              {rfqs.map((rfq) => (
                <div
                  key={rfq.id}
                  className="flex items-start justify-between gap-3 p-3 sm:p-4 bg-accent rounded-lg cursor-pointer hover:bg-accent/80"
                  onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm sm:text-base font-medium text-foreground break-words">{rfq.ref_code}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-2 break-words">{rfq.title}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                      Close Date: {formatDate(rfq.client_close_datetime, { style: 'short' })}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge className={`text-[10px] sm:text-xs ${getStatusBadgeColor(rfq.status)}`}>
                      {rfq.status.replace(/_/g, ' ')}
                    </Badge>
                    {rfq.clearance_required && (
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{rfq.clearance_required}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm sm:text-base text-muted-foreground">No RFQs for this client yet</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Client Modal */}
      <AddClientModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSuccess={() => {
          refetch();
          setEditModalOpen(false);
        }}
        editMode={true}
        existingClient={client}
      />
    </DetailContainer>
  );
};

export default ClientDetail;
